import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, Card, Button, Empty, Tag, Tabs, Typography, Space } from 'antd';
import { 
  ArrowLeftOutlined, 
  HeartOutlined, 
  UserOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import './IntroPage.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const MyReportsPage = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState({ self: [], partner: [] });

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = () => {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      return;
    }

    // 加载所有报告
    const selfReports = JSON.parse(localStorage.getItem(`rpi_reports_self_${sessionId}`) || '[]');
    const partnerReports = JSON.parse(localStorage.getItem(`rpi_reports_partner_${sessionId}`) || '[]');

    setReports({
      self: selfReports,
      partner: partnerReports
    });
  };

  const handleViewReport = (type, reportId) => {
    // 将 reportId 存储到 localStorage，ReportPage 会读取
    localStorage.setItem('currentReportId', reportId);
    navigate(`/report/${type}`);
  };

  const handleDeleteReport = (type, reportId) => {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) return;

    const key = `rpi_reports_${type}_${sessionId}`;
    const reports = JSON.parse(localStorage.getItem(key) || '[]');
    const filtered = reports.filter(r => r.id !== reportId);
    
    localStorage.setItem(key, JSON.stringify(filtered));
    loadReports();
  };

  const getLevelColor = (level) => {
    const colors = {
      '极低': '#52c41a',
      '较低': '#73d13d',
      '中等': '#faad14',
      '较高': '#ff7a45',
      '极高': '#f5222d'
    };
    return colors[level] || '#999';
  };

  const renderReportCard = (report, type) => (
    <Card 
      key={report.id}
      style={{ 
        marginBottom: 16,
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}
      hoverable
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {type === 'self' ? (
                <UserOutlined style={{ fontSize: 18, color: 'var(--rpi-primary)' }} />
              ) : (
                <HeartOutlined style={{ fontSize: 18, color: 'var(--rpi-primary)' }} />
              )}
              <Text strong style={{ fontSize: 16 }}>
                {type === 'self' ? '给自己测' : '为恋人测'}
              </Text>
              <Tag color={getLevelColor(report.level)} style={{ marginLeft: 8 }}>
                {report.level}
              </Tag>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Text type="secondary">
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {report.timestamp}
              </Text>
              <Text style={{ fontSize: 18, fontWeight: 600, color: 'var(--rpi-primary)' }}>
                RPI: {report.rpiIndex}
              </Text>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(report.dimensions).map(([key, value]) => {
                const names = {
                  control: '控制欲望',
                  jealousy: '嫉妒强度',
                  dependence: '情感依赖',
                  insecurity: '关系不安全感'
                };
                return (
                  <Tag key={key} color="blue">
                    {names[key]}: {value.score}
                  </Tag>
                );
              })}
            </div>
          </Space>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleViewReport(type, report.id)}
          >
            查看
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteReport(type, report.id)}
          />
        </div>
      </div>
    </Card>
  );

  const renderReportList = (reportList, type) => {
    if (reportList.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: '#999' }}>
              暂无{type === 'self' ? '自己的' : '恋人的'}测试报告
            </span>
          }
        >
          <Button type="primary" onClick={() => navigate('/')}>
            去测试
          </Button>
        </Empty>
      );
    }

    return reportList.map(report => renderReportCard(report, type));
  };

  return (
    <Layout className="rpi-intro-layout">
      <header className="rpi-header">
        <div className="rpi-header-content">
          <div className="rpi-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <div className="rpi-logo-icon">💕</div>
            <div className="rpi-logo-text">
              <div className="rpi-logo-title">RPI Calculator</div>
              <div className="rpi-logo-subtitle">恋爱占有欲指数计算器</div>
            </div>
          </div>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/')}
          >
            返回首页
          </Button>
        </div>
      </header>

      <Content style={{ padding: '40px 20px', maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ marginBottom: 8 }}>
            📊 我的测试报告
          </Title>
          <Text type="secondary">
            查看和管理您的历史测试记录
          </Text>
        </div>

        <Tabs defaultActiveKey="all" size="large">
          <TabPane tab="全部报告" key="all">
            <div style={{ marginBottom: 32 }}>
              <Title level={4} style={{ marginBottom: 16 }}>
                💖 给自己测 ({reports.self.length})
              </Title>
              {renderReportList(reports.self, 'self')}
            </div>

            <div>
              <Title level={4} style={{ marginBottom: 16 }}>
                💞 为恋人测 ({reports.partner.length})
              </Title>
              {renderReportList(reports.partner, 'partner')}
            </div>
          </TabPane>

          <TabPane tab={`给自己测 (${reports.self.length})`} key="self">
            {renderReportList(reports.self, 'self')}
          </TabPane>

          <TabPane tab={`为恋人测 (${reports.partner.length})`} key="partner">
            {renderReportList(reports.partner, 'partner')}
          </TabPane>
        </Tabs>
      </Content>
    </Layout>
  );
};

export default MyReportsPage;

